import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ThesisMarkdown from './ThesisMarkdown';
import ChartGrid from './ChartGrid';
import './ThesisMarketsPage.css';

const SOURCE_TYPE_ORDER = ['polymarket', 'news_article', 'tweet', 'fred_series', 'job_signal', 'bdc_signal'];
const SOURCE_TYPE_LABELS = {
  news_article: 'News',
  tweet: 'Tweets',
  polymarket: 'Prediction Markets',
  fred_series: 'Economic Data',
  job_signal: 'Hiring Signals',
  bdc_signal: 'BDC Signals',
};

export default function ThesisMarketsPage() {
  const [thesis, setThesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [highlightedSource, setHighlightedSource] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    fetch('/api/thesis/latest')
      .then(r => r.json())
      .then(data => { setThesis(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Extract chart symbols from thesis markdown (tickers mentioned in trades)
  const chartSymbols = useMemo(() => {
    if (!thesis?.thesis_markdown) return [];
    const tickerRegex = /\b(CL=F|BZ=F|GC=F|DX-Y\.NYB|UCO|USO|GLD|XLE|XOM|CVX|OWL|ARCC|FSK|NVDA|MSFT|QQQ|SPY|ITA|LMT|RTX|TLT|HYG|JETS)\b/g;
    const matches = [...new Set(thesis.thesis_markdown.match(tickerRegex) || [])];
    return matches.slice(0, 4);
  }, [thesis]);

  // Strip the markdown's own H1 title line (Claude writes its own header that may have wrong date)
  const cleanMarkdown = useMemo(() => {
    if (!thesis?.thesis_markdown) return '';
    return thesis.thesis_markdown
      .replace(/^#\s+Market\s+(?:Intelligence|Daily)\s+Brief[^\n]*\n*/im, '')
      .replace(/^\*Published[^\n]*\*\s*\n*/i, '')
      .replace(/^---\s*\n*/m, '');
  }, [thesis]);

  // Date from generated_at (use date portion only to avoid UTC rollover)
  const generatedDate = useMemo(() => {
    const gt = thesis?.generated_at;
    if (!gt) return '';
    const parts = gt.split('T')[0].split('-');
    if (parts.length === 3) {
      return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    return '';
  }, [thesis]);

  const handleCitationClick = useCallback((sourceId) => {
    setHighlightedSource(sourceId);
    const el = document.getElementById(`source-${sourceId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightedSource(null), 2000);
    }
  }, []);

  // Group sources by type
  const groupedSources = useMemo(() => {
    if (!thesis?.sources) return {};
    const groups = {};
    thesis.sources.forEach((s, i) => {
      const type = s.source_type;
      if (!groups[type]) groups[type] = [];
      groups[type].push({ ...s, _index: i });
    });
    return groups;
  }, [thesis]);

  if (loading) {
    return (
      <div className="thesis-page">
        <div className="thesis-page__loading">Loading intelligence brief...</div>
      </div>
    );
  }

  if (!thesis || !thesis.thesis_markdown) {
    return (
      <div className="thesis-page">
        <div className="thesis-page__empty">
          No thesis available. Run the GMR pipeline to generate one.
        </div>
      </div>
    );
  }

  return (
    <div className="thesis-page">
      {/* Header */}
      <header className="thesis-page__header">
        <h1 className="thesis-page__title">Market Daily Brief</h1>
        <div className="thesis-page__date">{generatedDate}</div>
        <div className="thesis-page__meta">
          {thesis.source_count} sources cited
          {thesis.data_summary && (
            <span className="thesis-page__meta-detail">
              {' '} &middot; {thesis.data_summary.headlines} articles
              {' '} &middot; {thesis.data_summary.tweets} tweets
              {' '} &middot; {thesis.data_summary.polymarket} markets
            </span>
          )}
        </div>
        <Link to="/markets/engine" className="thesis-page__engine-link">
          Powered by the ferl Signal Engine &rarr;
        </Link>
      </header>

      {/* Source type filters */}
      <div className="source-filters">
        <button
          className={`source-filter ${!activeFilter ? 'active' : ''}`}
          onClick={() => setActiveFilter(null)}
        >
          All ({thesis.sources.length})
        </button>
        {SOURCE_TYPE_ORDER.map(type => {
          const count = groupedSources[type]?.length || 0;
          if (!count) return null;
          return (
            <button
              key={type}
              className={`source-filter ${activeFilter === type ? 'active' : ''}`}
              onClick={() => setActiveFilter(activeFilter === type ? null : type)}
            >
              {SOURCE_TYPE_LABELS[type] || type} ({count})
            </button>
          );
        })}
      </div>

      {/* Single-column layout — sources render inline via ThesisMarkdown */}
      <div className="thesis-page__layout">
        <main className="thesis-page__content">
          <ThesisMarkdown
            markdown={cleanMarkdown}
            sources={thesis.sources}
            onCitationClick={handleCitationClick}
            activeFilter={activeFilter}
            highlightedSource={highlightedSource}
          />

          {/* Charts after the thesis */}
          {chartSymbols.length > 0 && (
            <section className="thesis-page__charts">
              <h2 className="thesis-section-heading">Trade Charts</h2>
              <ChartGrid symbols={chartSymbols} />
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
